import {computed, makeObservable} from "mobx";
import SaporeForm from "../SaporeForm";
import TextField from "./TextField";
import OptionField from "./OptionField";
import MultipleOptionsField from "./MultipleOptionField";

export default class DeliverySiteOnboardingRequestForm extends SaporeForm {
    static newEmpty(feature, configuration) {
        const initialData = {
            name: '',
            address: '',
            jurisdiction: '',
            city: '',
            postalCode: '',
            zone: '',
            preferredDeliveryTimeRange: '',
            exceptionalFrequency: ''
        };
        return new DeliverySiteOnboardingRequestForm(initialData, feature, null, configuration);
    }

    static newFor(deliverySiteOnboardingRequest, feature, configuration) {
        return new DeliverySiteOnboardingRequestForm(deliverySiteOnboardingRequest, feature, deliverySiteOnboardingRequest.id, configuration);
    }

    constructor(initialData = {}, feature, id=null, configuration) {
        const fields = {
            name: new TextField(2, 25, false, initialData.name || '', feature),
            address: new TextField(5, 200, false, initialData.address || '', feature),
            jurisdiction: new OptionField(feature.jurisdictions(), true, feature, initialData.jurisdiction || ''),
            city: new TextField(2, 50, false, initialData.city || '', feature),
            postalCode: new TextField(4, 10, false, initialData.postalCode || '', feature),
            zone: new OptionField(feature.zones(), true, feature, initialData.zone || ''),
            preferredDeliveryTimeRange: new TextField(5, 100, true, initialData.preferredDeliveryTimeRange || '', feature),
            exceptionalFrequency: new MultipleOptionsField(feature.frequencies(), true, feature, initialData.exceptionalFrequency || [])
        };
        super(fields, id, feature, configuration);
        makeObservable(this, {
            nameValue: computed,
            addressValue: computed,
            jurisdictionValue: computed,
            cityValue: computed,
            postalCodeValue: computed,
            preferredDeliveryTimeRangeValue: computed,
            zoneValue: computed,
            exceptionalFrequencyValue: computed,
        });
    }

    formEntity() {
        return {...this.values, id: this.entityId()}
    }

    name() {
        return this.nameField().value;
    }

    address() {
        return this.addressField().value;
    }

    jurisdiction() {
        return this.jurisdictionField().value;
    }

    city() {
        return this.cityField().value;
    }

    postalCode() {
        return this.postalCodeField().value;
    }

    preferredDeliveryTimeRange() {
        return this.preferredDeliveryTimeRangeField().value;
    }

    zone() {
        return this.zoneField().value;
    }

    exceptionalFrequency() {
        return this.exceptionalFrequencyField().value;
    }

    nameField() {
        return this.getFieldByName('name');
    }

    addressField() {
        return this.getFieldByName('address');
    }

    jurisdictionField() {
        return this.getFieldByName('jurisdiction');
    }

    cityField() {
        return this.getFieldByName('city');
    }

    postalCodeField() {
        return this.getFieldByName('postalCode');
    }

    preferredDeliveryTimeRangeField() {
        return this.getFieldByName('preferredDeliveryTimeRange');
    }

    zoneField() {
        return this.getFieldByName('zone');
    }

    exceptionalFrequencyField() {
        return this.getFieldByName('exceptionalFrequency');
    }

    get nameValue() {
        return this.name();
    }

    get addressValue() {
        return this.address();
    }

    get jurisdictionValue() {
        return this.jurisdiction();
    }

    get cityValue() {
        return this.city();
    }

    get postalCodeValue() {
        return this.postalCode();
    }

    get preferredDeliveryTimeRangeValue() {
        return this.preferredDeliveryTimeRange();
    }

    get zoneValue() {
        return this.zone();
    }

    get exceptionalFrequencyValue() {
        return this.exceptionalFrequency();
    }

    isReadonly(fieldName) {
        return !this._feature.canEditDeliverySitesOnboardingRequests();
    }

    async updateMultipleFieldsWithValues(fieldUpdates) {
        Object.entries(fieldUpdates).forEach(([fieldName, value]) => {
            this.getFieldByName(fieldName).updateValue(value);
        });

        if (this._feature && this.hasAllValidValues()) {
            await this._feature.formChanged(this.formEntityWithFormId());
        }
    }
}
